import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import LingoFlowIcon from '@/components/LingoFlowIcon.tsx'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select.tsx'
import { cn } from '@/lib/utils.ts'
import useGlobalStore from '@/useGlobalStore.ts'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button.tsx'
import PrivacyEulaLinks from '@/components/privacy-eula-links.tsx'

const PageOnboarding = ({ setPreferencesLoaded }: { setPreferencesLoaded: () => void; preferencesLoaded: boolean }) => {
	const { t } = useTranslation()
	const {
		appLanguageCode,
		learningLanguageCode,
		availableLearningLanguages,
		availableAppLanguages,
		changeLearningLanguage,
		changeAppLanguage,
	} = useGlobalStore()
	const [loading, setLoading] = useState(true)
	const [expanded, setExpanded] = useState(false)

	useEffect(() => {
		setTimeout(() => {
			setLoading(false)
			setTimeout(() => setExpanded(true), 100)
		}, 2000)
	}, [])

	const containerVariants = {
		loading: {
			width: '6rem',
			height: '6rem',
			borderRadius: '10%',
			y: '40vh',
			scale: 1,
		},
		expanded: {
			width: '100vw',
			height: '100vh',
			borderRadius: '0%',
			y: 0,
			scale: 1,
			transition: {
				duration: 0.8,
				ease: [0.6, 0.01, -0.05, 0.9],
				backgroundColor: { delay: 0.2 },
			},
		},
	}

	const cubeVariants = {
		spin: {
			rotate: [0, 360],
			transition: {
				duration: 0.8,
				ease: 'easeInOut',
				repeat: Infinity,
				repeatDelay: 0.2,
			},
		},
	}

	const formVariants = {
		hidden: {
			opacity: 0,
			y: 100,
		},
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				delay: 0.4,
				duration: 0.6,
				type: 'spring',
				stiffness: 200,
				damping: 20,
			},
		},
	}

	return (
		<div className="h-screen w-screen flex items-center justify-center dark:bg-dot-white/[0.2] bg-dot-black/[0.2] overflow-x-hidden">
			<div className="absolute pointer-events-none inset-0 flex items-center justify-center dark:bg-black bg-white [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)]"></div>
			<motion.div
				className={`flex items-center justify-center overflow-hidden fixed bg-background`}
				initial="loading"
				animate={expanded ? 'expanded' : 'loading'}
				variants={containerVariants}>
				<AnimatePresence mode="wait">
					{loading ? (
						<motion.div
							key="cube"
							variants={cubeVariants}
							animate={loading ? 'spin' : 'static'}
							className="p-4 rounded">
							<LingoFlowIcon className="w-8 h-8" />
						</motion.div>
					) : (
						<motion.div
							className="w-full max-w-md p-8"
							variants={formVariants}
							initial="hidden"
							animate={expanded ? 'visible' : 'hidden'}>
							<div className="flex flex-col gap-2 mb-5">
								<h2 className="text-lg font-semibold">{t('Hi')}</h2>
								<div>{t('Welcome to LingoFlow AI')}</div>
								<div className="my-4">
									{t(
										'Before getting started, please specify your native language and the main language you are trying to learn.',
									)}
								</div>
							</div>
							<form className="space-y-4">
								<div>
									<div className="text-xs font-semibold mb-1">
										{t('Your native language or one you are fluent in')}:
									</div>
									<Select
										defaultValue={appLanguageCode ?? null}
										onValueChange={(lang) => changeAppLanguage(lang)}>
										<SelectTrigger
											className={cn(
												'flex items-center gap-2 [&>span]:line-clamp-1 [&>span]:flex w-full [&>span]:w-full [&>span]:items-center [&>span]:gap-1 [&>span]:truncate [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0',
											)}
											aria-label="Select account">
											<SelectValue placeholder="Select a language">
												<span
													className={`w-5 fi fi-${availableAppLanguages[appLanguageCode]?.icon}`}></span>
												<span className={cn('ml-2')}>
													{availableAppLanguages[appLanguageCode]?.name}
												</span>
											</SelectValue>
										</SelectTrigger>
										<SelectContent>
											{Object.entries(availableAppLanguages).map(([key, lang]) => (
												<SelectItem key={key} value={key}>
													<div className="flex items-center gap-3 [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0 [&_svg]:text-foreground">
														<span className={`w-5 fi fi-${lang.icon}`}></span>
														{lang.name}
													</div>
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								<div>
									<div className="text-xs font-semibold mb-1">
										{t('A language you want to learn')}:
									</div>
									<Select
										defaultValue={learningLanguageCode ?? null}
										onValueChange={(lang) => changeLearningLanguage(lang)}>
										<SelectTrigger
											className={cn(
												'flex items-center gap-2 [&>span]:line-clamp-1 [&>span]:flex w-full [&>span]:w-full [&>span]:items-center [&>span]:gap-1 [&>span]:truncate [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0',
											)}
											aria-label="Select account">
											<SelectValue placeholder={t('Select a language')}>
												<span
													className={`w-5 fi fi-${availableLearningLanguages[learningLanguageCode]?.icon}`}></span>
												<span className={cn('ml-2')}>
													{availableLearningLanguages[learningLanguageCode]?.name}
												</span>
											</SelectValue>
										</SelectTrigger>
										<SelectContent>
											{Object.entries(availableLearningLanguages).map(([key, lang]) => (
												<SelectItem key={key} value={key}>
													<div className="flex items-center gap-3 [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0 [&_svg]:text-foreground">
														<span className={`w-5 fi fi-${lang.icon}`}></span>
														{lang.name}
													</div>
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								<div className="text-sm">
									{t(
										'These settings can be changed later. You can learn multiple languages at the same time.',
									)}
								</div>
								{appLanguageCode !== null &&
									appLanguageCode !== '' &&
									learningLanguageCode !== null &&
									learningLanguageCode !== '' && (
										<div>
											<Button
												onClick={setPreferencesLoaded}
												variant="default"
												className="w-full font-bold">
												{t('Launch LingoFlow')}
												<LingoFlowIcon className="h-4 w-4 ms-1 -mt-1" invert={true} />
											</Button>
										</div>
									)}
								<PrivacyEulaLinks className="pt-4" />
							</form>
						</motion.div>
					)}
				</AnimatePresence>
			</motion.div>
		</div>
	)
}

export default PageOnboarding
